// FloorStack — visual diamond stack of floor cards.
//
// Layout: cards stacked along Y, top floor = first child. Each subsequent
// card is positioned ~110px below (z-axis simulation via overlap).
// Selected floor (during expanding/expanded) is brought in front and
// siblings translate outward (top siblings up, bottom siblings down).
//
// LIFF mobile fallback: the parent passes `mode="list"` and we render a
// vertical list with no rotation. (See SiteFloorStack mobile detection.)

import React from "react";
import FloorCard, { type CardPhase } from "./FloorCard.js";
import type { BuildingFloorDto, FloorStatusSnapshotDto } from "../lib/floorStackApi.js";
import type { FloorStackState } from "./state/floorStackMachine.js";
import { TIMINGS } from "./state/floorStackMachine.js";

interface Props {
  floors: Array<BuildingFloorDto & { latest_snapshot: FloorStatusSnapshotDto | null }>;
  state: FloorStackState;
  reduced: boolean;
  onHover: (id: string) => void;
  onUnhover: () => void;
  onClick: (id: string) => void;
  mode?: "stack" | "list";
}

export default function FloorStack({ floors, state, reduced, onHover, onUnhover, onClick, mode = "stack" }: Props): React.ReactElement {
  // Sort: lowest display_order first → render top floor first (highest)
  // by reversing so visually the top floor sits at the top of the stack.
  const sorted = [...floors].sort((a, b) => a.display_order - b.display_order);
  const stackDescending = [...sorted].reverse(); // index 0 = top floor visually

  const phaseFor = (floorId: string, idx: number, selectedIdx: number | null): CardPhase => {
    if (state.kind === "expanding" || state.kind === "expanded" || state.kind === "collapsing") {
      if (state.floorId === floorId) {
        if (state.kind === "expanding") return "selected";
        if (state.kind === "expanded") return "expanded";
        return "collapsing";
      }
      return "dim";
    }
    if (state.kind === "hovering" && state.floorId === floorId) return "hover";
    return "idle";
  };

  if (mode === "list") {
    // Mobile / reduced UI: vertical list, no rotation, just status colors.
    return (
      <ul className="space-y-2" data-floor-stack-mode="list">
        {stackDescending.map((f) => (
          <li key={f.id}>
            <button
              type="button"
              onClick={() => onClick(f.id)}
              data-floor-id={f.id}
              className="w-full rounded border border-border bg-bg-surface px-4 py-3 text-left flex items-center justify-between gap-3 hover:bg-bg-hover transition-colors"
            >
              <div className="flex flex-col">
                <span className="text-base font-mono text-text-primary">{f.floor_name}</span>
                {f.latest_snapshot && (
                  <span className="text-xs text-text-secondary">
                    {f.latest_snapshot.completed}/{f.latest_snapshot.location_total} 完成
                  </span>
                )}
              </div>
              <div className="flex flex-col items-end">
                <span className={`badge-${f.latest_snapshot?.summary_status ?? "default"} text-xs`}>
                  {f.latest_snapshot?.summary_status ?? "idle"}
                </span>
                {f.latest_snapshot && (f.latest_snapshot.incident_warning_count + f.latest_snapshot.incident_critical_count) > 0 && (
                  <span className="text-xs text-amber-400">⚠ {f.latest_snapshot.incident_warning_count + f.latest_snapshot.incident_critical_count}</span>
                )}
              </div>
            </button>
          </li>
        ))}
      </ul>
    );
  }

  const selectedIdx = state.kind === "expanding" || state.kind === "expanded" || state.kind === "collapsing"
    ? stackDescending.findIndex((f) => f.id === state.floorId)
    : null;

  return (
    <div
      className="floor-stack relative mx-auto select-none"
      data-floor-stack-mode="stack"
      style={{
        // Container needs to be wide enough for the rotated rhombus + lifts.
        width: "300px",
        height: `${Math.max(360, stackDescending.length * 110 + 220)}px`,
        perspective: "1200px",
      }}
    >
      {stackDescending.map((f, idx) => {
        const phase = phaseFor(f.id, idx, selectedIdx);
        // Base Y position — top of stack near top of container.
        let translateY = 80 + idx * 80; // spacing between cards (compressed for diamond overlap)
        // During selected/expanded, push siblings away to clear the chosen card.
        if (selectedIdx !== null && idx !== selectedIdx) {
          const offset = (idx < selectedIdx ? -180 : 180);
          if (state.kind === "expanding" || state.kind === "expanded") {
            translateY += offset;
          } else if (state.kind === "collapsing") {
            translateY += offset * 0.4; // ease back in
          }
        }
        const stagger = idx * TIMINGS.staggerMs;
        const z = 100 - idx + (idx === selectedIdx ? 1000 : 0);
        return (
          <div
            key={f.id}
            className="absolute left-1/2 -translate-x-1/2"
            style={{
              top: `${translateY}px`,
              zIndex: z,
              transition: reduced ? undefined : `top 360ms cubic-bezier(0.22,1,0.36,1) ${stagger}ms`,
            }}
          >
            <FloorCard
              floor={f}
              snapshot={f.latest_snapshot}
              phase={phase}
              reduced={reduced}
              onHover={() => onHover(f.id)}
              onUnhover={() => onUnhover()}
              onClick={() => onClick(f.id)}
            />
          </div>
        );
      })}
    </div>
  );
}
